import { InitialFields }               from '@widesk-ui/components/FilterBar';
import React                           from 'react';
import TableStore                      from '@widesk-ui/stores/TableStore';
import { observer }                    from 'mobx-react-lite';
import { DndContext }                  from '@dnd-kit/core';
import { restrictToVerticalAxis }      from '@dnd-kit/modifiers';
import type { SyntheticListenerMap }   from '@dnd-kit/core/dist/hooks/utilities';
import { CSS }                         from '@dnd-kit/utilities';
import { SortableContext }             from '@dnd-kit/sortable';
import { verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable }                 from '@dnd-kit/sortable';
import TableContent                    from '@widesk-ui/components/table/TableContent';
import { TableProps } from '@widesk-ui/components/table/Table';
import DragHandle     from '../DragHandle/DragHandle';

interface RowContextProps {
	setActivatorNodeRef?: (element: HTMLElement | null) => void;
	listeners?: SyntheticListenerMap;
}

interface RowProps extends React.HTMLAttributes<HTMLTableRowElement> {
	'data-row-key': string;
}

const RowContext = React.createContext<RowContextProps>({});

const Row: React.FC<RowProps> = (props) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: props['data-row-key'] });

	const style: React.CSSProperties = {
		...props.style,
		transform: CSS.Translate.toString(transform),
		transition,
		...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
	};

	const contextValue = React.useMemo<RowContextProps>(
		() => ({ setActivatorNodeRef, listeners }),
		[setActivatorNodeRef, listeners],
	);

	return (
		<RowContext.Provider value={contextValue}>
			<tr {...props} ref={setNodeRef} style={style} {...attributes} />
		</RowContext.Provider>
	);
};

const PositionTableContent = observer(<D extends any[], F extends InitialFields>(props: TableProps<D, F> & {
	tableStore: TableStore;
}) => {
	return (
		<DndContext
			modifiers={[restrictToVerticalAxis]}
			onDragEnd={async ({ active, over }) => {
				if (active.id !== over?.id) {
					// On met à jour optimistiquement l'UI
					const activeIndex = props.tableStore.data.findIndex(record => {
						const key = typeof props.rowKey === 'function' ? props.rowKey(record) : record[props.rowKey];
						return key === active?.id;
					});
					const overIndex = props.tableStore.data.findIndex(record => {
						const key = typeof props.rowKey === 'function' ? props.rowKey(record) : record[props.rowKey];
						return key === over?.id;
					});

					// On met à jour optimistiquement l'UI en modifiant le tableau existant
					const movedItem = props.tableStore.data[activeIndex];
					props.tableStore.data.splice(activeIndex, 1);
					props.tableStore.data.splice(overIndex, 0, movedItem!);

					return props.onChangePosition?.({
						position: over?.data.current?.sortable.index,
						oldPosition: active.data.current?.sortable.index,
						store: props.tableStore,
					});
				}
			}}
		>
			<SortableContext
				items={props.tableStore.data.map(d => {
					const key = typeof props.rowKey === 'function' ? props.rowKey(d) : d[props.rowKey];
					return String(key);
				})}
				strategy={verticalListSortingStrategy}
			>
				<TableContent
					{...props}
					columns={[
						{
							reference: 'sortable-column',
							title: '',
							align: 'center',
							width: 60,
							render: datum => (
								<DragHandle
									RowContext={RowContext}
									datum={datum}
									onChange={props.onChangePosition}
									store={props.tableStore}
								/>
							),
						},
						...props.columns,
					]}
					components={{
						body: { row: Row },
					}}
				/>
			</SortableContext>
		</DndContext>
	);
});

export default PositionTableContent;
